import React, { FunctionComponent, useState } from 'react';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ScienceIcon from '@mui/icons-material/Science';

import {
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import {
  Button,
  DataGrid,
  DatePicker,
  Dt,
  Fab,
  FileUpload,
  IconButton,
  Page,
  Typography,
} from '../../../components/v2/styled';
import { SnackDestroyed } from '../../../components/v2/snacks/Destroyed';
import { SnackSaved } from '../../../components/v2/snacks/Saved';

import { fetch_all_with_count, fetch_one } from '../../../lib/v31lib';
import { loginAtom } from '../../../lib/auth';

const { DateTime } = require("luxon");

type NevoImport = {
  id?: number;
  status?: string;
  source?: string;
  source_date?: string;
  error_message?: string;
  extra_data?: {
    counts?: {
      [key: string]: number;
    };
  };
  item_file_name?: string;
  porties_file_name?: string;
  user?: {
    id?: number;
    full_name?: string;
  };
  created_at?: string;
  item?: File;
  porties?: File;
}

const statusColors: { [status: string]: string } = {
  new: 'text.primary',
  parsed: 'primary.main',
  committed: 'success.main',
  error: 'error.main',
};

type Props = {}

export const AdminFoodCalculationNevoImports: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const {t, i18n} = useTranslation(['translations']);
  const [destroyedOpen, setDestroyedOpen] = useState<boolean>(false);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [newImport, setNewImport] = useState<NevoImport>();

  let count = 0;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("id DESC");

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["admin", "nevo_imports", {page: page, order: order}],
    queryFn: () =>
      fetch_all_with_count<NevoImport>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'nevo_import',
          fields: ['id', 'status', 'source', 'source_date', 'error_message', 'extra_data', 'created_at', 'item_file_name', 'porties_file_name', 'user|id/full_name'],
          order: order,
          per_page: 20,
          page: page,
          filter: {
            search: '',
            advanced: {}
          }
        },
        login
      ),
    select: (d) => {
      count = d.count;
      return d.result;
    },
    enabled: !!login?.id
  });

  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<NevoImport>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      objectsRefetch();
    },
  });

  const mutationDestroy = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<NevoImport>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/remove`,
        pars.formData,
        login
      );
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setDestroyedOpen(true);
      objectsRefetch();
    },
  });

  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<NevoImport>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      mutationCustomAction.mutate({
        formData: {
          object: 'nevo_import',
          class_action: 'custom_api_parse',
          id: data.id
        }
      });
      setNewImport(undefined);
      objectsRefetch();
    },
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'item_file_name', headerName: t("admin.models.nevo_imports.source"), width: 280 },
    { field: 'status', headerName: t("admin.nevo_imports.status"), width: 130, renderCell: params => <>
      <Typography sx={{color: statusColors[params.row.status] || 'text.primary'}}>{t(`admin.nevo_imports.status.${params.row.status}`)}</Typography>
    </> },
    { field: 'source_date', headerName: t("admin.nevo_imports.source_date"), width: 130, renderCell: params => <>
      {!!params.row.source_date && <Dt d={params.row.source_date} f="DDD" />}
    </> },
    { field: 'created_at', headerName: t("admin.models.nevo_imports.created_at"), width: 160, renderCell: params => <>
      {!!params.row.created_at && <Dt d={params.row.created_at} f="DDD t" />}
    </> },
    { field: 'counts_products', headerName: t("admin.nevo_imports.counts.products"), width: 110, renderCell: params => <>
      <Typography>{params.row.counts_products}</Typography>
    </> },
    { field: 'counts_measures', headerName: t("admin.nevo_imports.counts.measures"), width: 100, renderCell: params => <>
      <Typography>{params.row.counts_measures}</Typography>
    </> },
    { field: 'counts_to_create', headerName: t("admin.nevo_imports.counts.to_create"), width: 120, renderCell: params => <>
      <Typography>{params.row.counts_to_create}</Typography>
    </> },
  ];

  return <Page title={t("admin.menu.nevo_imports")}>
    {!!newImport && <>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FileUpload
            caption={t("admin.nevo_imports.file")}
            fileInputName='item'
            accept=".xlsx"
            selected={newImport.item}
            onChange={(f) => {
              setNewImport({
                ...newImport,
                item: f as File
              });
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <FileUpload
            caption={t("admin.nevo_imports.porties")}
            fileInputName='porties'
            accept=".zip"
            selected={newImport.porties}
            onChange={(f) => {
              setNewImport({
                ...newImport,
                porties: f as File
              });
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{
            fontSize: '14px'
          }}>{t("admin.nevo_imports.source_date")}</Typography>
          <DatePicker
            disableFuture={false}
            openTo="year"
            label=""
            format={"dd/LL/y"}
            views={["year", "month", "day"]}
            setValue={(e) => {
              setNewImport({
                ...newImport,
                source_date: e
              });
            }}
            value={newImport?.source_date || DateTime.now().toFormat("dd/LL/y")}
            id="source_date"
            sx={{
              box: {
                width: 'auto',
                display: 'inline-block'
              },
              field_box: {
                "svg": {
                  display: 'none'
                }
              },
              field: {
                width: 'auto',
                marginTop: 0,
                "input": {
                  textAlign: 'center'
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sx={{
          alignContent: 'baseline',
          textAlign: 'center',
          position: {
              xs: 'relative',
              md: 'sticky'
            },
          bottom: '-24px',
          backgroundColor: 'white',
          zIndex: 1201
        }}>
          <Divider sx={{marginTop: 1, marginBottom: 0}} />

          {!!mutationSave.isPending && <CircularProgress />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.save")}
            id='save'
            disabled={!newImport.item}
            contained
            onClick={(v) => {
              let data = new FormData();
              data.append('object', 'nevo_import');
              data.append('fields', 'id');
              data.append('ob[source_date]', newImport.source_date || DateTime.now().toFormat("dd/LL/y"));
              if (!!newImport.item) {
                data.append(`ob[item]`, newImport.item);
              }
              if (!!newImport.porties) {
                data.append(`ob[porties]`, newImport.porties);
              }

              mutationSave.mutate({
                formData: data
              });
            }}
            sx={{marginBottom: 1, marginTop: 1}}
          />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.cancel")}
            id='cancel'
            text
            onClick={(v) => {
              setNewImport(undefined);
            }}
            sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
          />}
        </Grid>
      </Grid>
    </>}

    {!newImport && <DataGrid
      action_count={3}
      actions={(params) => {
        return (<>
          {(params.row.status === 'new' || params.row.status === 'error') && <IconButton title={t("admin.nevo_imports.parse")} color="default" onClick={() => {
            mutationCustomAction.mutate({
              formData: {
                object: 'nevo_import',
                class_action: 'custom_api_parse',
                id: params.row.id
              }
            });
          }}>
            <PlayCircleIcon />
          </IconButton>}
          {params.row.status === 'parsed' && <IconButton title={t("admin.nevo_imports.commit_dry_run")} color="default" onClick={() => {
            mutationCustomAction.mutate({
              formData: {
                object: 'nevo_import',
                class_action: 'custom_api_commit',
                id: params.row.id,
                dry_run: 1
              }
            });
          }}>
            <ScienceIcon />
          </IconButton>}
          {(params.row.status === 'parsed' || params.row.status === 'committed') && <IconButton title={t("admin.nevo_imports.commit")} color="default" onClick={() => {
            if (window.confirm("Voedingsmiddelen, waardes en maten aanmaken of aanpassen?")) {
              mutationCustomAction.mutate({
                formData: {
                  object: 'nevo_import',
                  class_action: 'custom_api_commit',
                  id: params.row.id
                }
              });
            }
          }}>
            <DirectionsRunIcon />
          </IconButton>}
          <IconButton title="Remove" destroyBtn color="default" onClick={() => {
            mutationDestroy.mutate({
              formData: {
                object: 'nevo_import',
                id: params.row.id,
                ob: {
                  id: params.row.id,
                }
              }
            });
          }}>
            <DeleteIcon />
          </IconButton>
        </>);
      }}
      objectCount={count}
      objects={(objects || []).map(u => ({
        ...u,
        counts_products: !!u.extra_data?.counts ? (u.extra_data.counts.products ?? '-') : '-',
        counts_measures: !!u.extra_data?.counts ? (u.extra_data.counts.measures ?? '-') : '-',
        counts_to_create: !!u.extra_data?.counts ? (u.extra_data.counts.to_create ?? '-') : '-',
      }))}
      columns={columns}
      loader={objectsLoading}
      onSortModelChange={(a, b) => {
        let a_0_field:string = a[0].field;
        setOrder([a_0_field, a[0].sort].join(' '));
      }}
      page={page}
      setPage={(p) => {setPage(p);}}
    />}

    <SnackDestroyed open={destroyedOpen} setOpen={setDestroyedOpen} />
    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />

    {!newImport && <Fab
      onClick={() => {
        setNewImport({});
      }}
    />}
  </Page>;
}
